import {
  ComponentPropsWithoutRef,
  forwardRef,
  MutableRefObject,
  Ref,
  useEffect,
  useRef,
} from 'react'

import { Listbox } from '@headlessui/react'
import { clsx } from 'clsx'

import { SvgIcon } from '../Icon/SvgIcon.js'
import {
  inputAriaExpandedStyles,
  inputIconStyles,
  inputStyles,
} from '../Input/index.js'
import { useFloatingContext } from '../utils/floating/index.js'

import { useSelect } from './SelectContext.js'

export type SelectButtonProps = ComponentPropsWithoutRef<'button'> & {
  clearable?: boolean
  onCleared?: () => void
}

const assignRef = <T,>(ref: Ref<T> | undefined, value: T | null) => {
  if (typeof ref === 'function') {
    ref(value)
  } else if (ref) {
    ;(ref as MutableRefObject<T | null>).current = value
  }
}

/**
 * Headless UI overwrites `aria-labelledby` unless a `Listbox.Label` is rendered,
 * so the external label is applied on the rendered element instead.
 */
const LabelledButton = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<'button'> & { labelledBy?: string }
>(({ labelledBy, ...props }, ref) => (
  <button
    type="button"
    ref={ref}
    {...props}
    aria-labelledby={
      props['aria-labelledby'] ??
      (labelledBy ? `${labelledBy} ${props.id}` : undefined)
    }
  />
))

export const SelectButton = ({
  children,
  className,
  placeholder,
  clearable,
  onCleared,
  ...rest
}: SelectButtonProps) => {
  const {
    invalid,
    disabled,
    buttonRef,
    ariaInvalid,
    ariaDescribedBy,
    ariaLabelledBy,
  } = useSelect()
  const { reference } = useFloatingContext()
  const buttonElementRef = useRef<HTMLButtonElement>(null)

  // Headless UI keeps one stable ref callback, so `buttonRef` would only be called on mount.
  // Re-sync every render: react-hook-form drops registered elements on reset().
  useEffect(() => {
    assignRef(buttonRef, buttonElementRef.current)
    return () => assignRef(buttonRef, null)
  })

  // TODO support compact size
  const size = 'default'
  const showClearButton = clearable && children

  return (
    <div ref={reference}>
      <Listbox.Button
        as={LabelledButton}
        ref={buttonElementRef}
        labelledBy={ariaLabelledBy}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        className={clsx(
          inputStyles({
            size,
            invalid,
            disabled,
            className,
          }),
          inputAriaExpandedStyles({ invalid }),
          'pe-10 text-start cursor-default',
        )}
        {...rest}
      >
        {() => (
          <>
            <span className="block truncate">
              {children || (
                <div className="text-content-disabled text-label-md">
                  {placeholder || '\u00A0'}
                </div>
              )}
            </span>
            {!showClearButton ? (
              <span
                className={clsx(
                  'absolute inset-y-0 end-0 flex items-center pe-2 pointer-events-none',
                )}
              >
                <SvgIcon
                  className={inputIconStyles({ size })}
                  aria-hidden="true"
                  name="selector"
                />
              </span>
            ) : (
              <button
                type="button"
                className="absolute inset-y-0 end-0 flex items-center px-2 rounded-base"
                aria-label="clear selection"
                onClick={e => {
                  e.stopPropagation()
                  onCleared?.()
                }}
              >
                <SvgIcon
                  className={inputIconStyles({ size })}
                  aria-hidden="true"
                  name="close"
                />
              </button>
            )}
          </>
        )}
      </Listbox.Button>
    </div>
  )
}
